type GooglePayCaptureState = {
  activeRequest: PaymentRequest | null;
  requests: Set<PaymentRequest>;
};

type CapturedPaymentRequestConstructor = typeof PaymentRequest & {
  __gpayCaptured?: boolean;
  __gpayCaptureState?: GooglePayCaptureState;
};

type GooglePayCaptureWindow = Window & {
  __dominosGooglePayCaptureState?: GooglePayCaptureState;
};

export type GooglePayDismissResult = {
  abortAttempted: boolean;
  abortResolved: boolean;
  domRemoved: number;
};

const GPAY_CAPTURE_STATE_KEY = "__dominosGooglePayCaptureState";

function getGooglePayCaptureState(): GooglePayCaptureState | null {
  if (typeof window === "undefined") return null;
  const w = window as GooglePayCaptureWindow;
  w[GPAY_CAPTURE_STATE_KEY] ??= {
    activeRequest: null,
    requests: new Set<PaymentRequest>(),
  };
  w[GPAY_CAPTURE_STATE_KEY].requests ??= new Set<PaymentRequest>();
  return w[GPAY_CAPTURE_STATE_KEY] ?? null;
}

function queryAllDeep(root: Document | ShadowRoot): Element[] {
  const elements = Array.from(root.querySelectorAll("*"));
  elements.forEach((el) => {
    if (el.shadowRoot) elements.push(...queryAllDeep(el.shadowRoot));
  });
  return elements;
}

function getBodyChildFor(element: Element): HTMLElement | null {
  let current: Element | null = element;

  while (current) {
    if (current.parentElement === document.body) {
      return current instanceof HTMLElement ? current : null;
    }
    current = current.parentElement;
  }

  const root = element.getRootNode();
  if (root instanceof ShadowRoot) {
    return getBodyChildFor(root.host);
  }

  return element instanceof HTMLElement ? element : null;
}

function getAttributeSnapshot(element: Element): string {
  const attrs = Array.from(element.attributes)
    .map((attr) => `${attr.name}=${attr.value}`)
    .join(" ");
  return `${element.tagName} ${attrs}`.toLowerCase();
}

function looksGooglePayOwned(element: Element): boolean {
  return /pay\.google\.com|googlepay|google-pay|\bgpay\b|\/gp\/p\//.test(
    getAttributeSnapshot(element),
  );
}

function isOverlayCandidate(element: Element): boolean {
  return (
    element.tagName === "IFRAME" ||
    element.matches('[role="dialog"], [aria-modal="true"]') ||
    isLikelyModalOverlay(element)
  );
}

function isLikelyModalOverlay(element: Element): boolean {
  if (!(element instanceof HTMLElement)) return false;
  const style = window.getComputedStyle(element);
  const zIndex = Number.parseInt(style.zIndex || "0", 10);
  const rect = element.getBoundingClientRect();
  const viewportArea = Math.max(window.innerWidth * window.innerHeight, 1);
  const elementArea = rect.width * rect.height;

  return (
    (style.position === "fixed" || style.position === "absolute") &&
    (Number.isNaN(zIndex) || zIndex >= 1000) &&
    elementArea / viewportArea > 0.25
  );
}

function removeElementRoot(element: Element, roots: Set<HTMLElement>): void {
  const bodyChild = getBodyChildFor(element);
  if (bodyChild && bodyChild !== document.body && bodyChild.isConnected) {
    roots.add(bodyChild);
  }
}

/**
 * Wrap window.PaymentRequest before Google Pay's SDK can cache it, so the
 * browser-native payment sheet can be aborted later.
 */
export function installPaymentRequestCapture(): void {
  if (typeof window === "undefined") return;
  const state = getGooglePayCaptureState();
  const Native = window.PaymentRequest as
    | CapturedPaymentRequestConstructor
    | undefined;

  if (!Native || !state) return;
  if (Native.__gpayCaptured && Native.__gpayCaptureState === state) return;

  const Wrapped = function (
    this: unknown,
    ...args: ConstructorParameters<typeof PaymentRequest>
  ) {
    const instance = new Native(...args);
    state.activeRequest = instance;
    state.requests.add(instance);
    return instance;
  } as unknown as CapturedPaymentRequestConstructor;

  Wrapped.prototype = Native.prototype;
  Object.setPrototypeOf(Wrapped, Native);
  Wrapped.__gpayCaptured = true;
  Wrapped.__gpayCaptureState = state;

  try {
    (
      window as Window & { PaymentRequest: CapturedPaymentRequestConstructor }
    ).PaymentRequest = Wrapped;
  } catch {
    /* Some browsers expose PaymentRequest as read-only. */
  }
}

function sweepGooglePayDom(): number {
  if (typeof document === "undefined") return 0;
  const roots = new Set<HTMLElement>();
  const elements = queryAllDeep(document);

  elements.forEach((element) => {
    if (looksGooglePayOwned(element) && isOverlayCandidate(element)) {
      removeElementRoot(element, roots);
      return;
    }

    if (
      element.tagName === "IFRAME" &&
      (isLikelyModalOverlay(element) ||
        !!element.closest('[role="dialog"], [aria-modal="true"]'))
    ) {
      removeElementRoot(element, roots);
    }
  });

  Array.from(document.body.children).forEach((child) => {
    if (
      child instanceof HTMLElement &&
      isLikelyModalOverlay(child) &&
      (child.querySelector("iframe") ||
        child.matches('[role="dialog"], [aria-modal="true"]'))
    ) {
      roots.add(child);
    }
  });

  roots.forEach((root) => {
    root.remove();
  });
  return roots.size;
}

export async function dismissGooglePaySheet(
  trackedNodes: Element[] = [],
): Promise<GooglePayDismissResult> {
  const state = getGooglePayCaptureState();
  const requests = Array.from(
    new Set(
      [state?.activeRequest, ...(state?.requests ? Array.from(state.requests) : [])].filter(
        Boolean,
      ) as PaymentRequest[],
    ),
  );
  let abortResolved = false;
  for (const request of requests) {
    try {
      await request.abort?.();
      abortResolved = true;
    } catch {
      /* Already settled or not abortable; keep trying other captured requests. */
    }
  }
  if (state) state.activeRequest = null;
  state?.requests.clear();

  const trackedRoots = new Set<HTMLElement>();
  trackedNodes.forEach((el) => {
    if (
      el.isConnected &&
      ((looksGooglePayOwned(el) && isOverlayCandidate(el)) ||
        isLikelyModalOverlay(el) ||
        !!el.querySelector?.("iframe"))
    ) {
      removeElementRoot(el, trackedRoots);
    }
  });
  trackedRoots.forEach((root) => root.remove());

  const immediateDomRemoved = sweepGooglePayDom();
  window.requestAnimationFrame(() => sweepGooglePayDom());
  [50, 150, 350, 750].forEach((delay) => {
    window.setTimeout(() => sweepGooglePayDom(), delay);
  });

  return {
    abortAttempted: requests.length > 0,
    abortResolved,
    domRemoved: trackedRoots.size + immediateDomRemoved,
  };
}
